import { useEffect, useState } from "react";
import { resolveWelcomePacket, WelcomePacketSection } from "@/lib/welcomePacket";

interface Props {
  classSlug: string;
  sessionLabel?: string | null;
  title?: string;
  compact?: boolean;
}

/**
 * Read-only welcome packet renderer — the same resolved content the email
 * uses. Shared by the parent registration detail page, the admin roster and
 * instructor class information so nothing is duplicated.
 */
export function WelcomePacketView({
  classSlug,
  sessionLabel = null,
  title = "Class Welcome Packet",
  compact = false,
}: Props) {
  const [sections, setSections] = useState<WelcomePacketSection[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    resolveWelcomePacket(classSlug, sessionLabel).then((rows) => {
      if (!cancelled) setSections(rows);
    });
    return () => {
      cancelled = true;
    };
  }, [classSlug, sessionLabel]);

  if (!sections || sections.length === 0) return null;

  return (
    <div className={compact ? "space-y-3" : "space-y-4"}>
      <h3 className={compact ? "text-sm font-semibold" : "text-lg font-semibold"}>{title}</h3>
      <div className="space-y-3">
        {sections.map((s) => (
          <div key={s.key} className="rounded-lg border bg-card p-4 text-left">
            <p className="text-sm font-medium">{s.heading}</p>
            {(s.content ?? "")
              .split(/\n+/)
              .map((line) => line.trim())
              .filter(Boolean)
              .map((line, i) => (
                <p key={i} className="text-sm text-muted-foreground mt-1 whitespace-pre-line">
                  {line}
                </p>
              ))}
            {s.linkUrl ? (
              <a
                href={s.linkUrl}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-primary underline mt-2 inline-block"
              >
                {s.linkLabel?.trim() || s.linkUrl}
              </a>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

export default WelcomePacketView;
